export type DmcaMailTemplateParams = {
  requestId: string;
  claimantName: string;
  statusLabel: string;
  infringingUrl: string;
  adminNotes?: string | null;
  counterNoticeUrl?: string;
};

export function buildDmcaIntakeMail(params: {
  requestId: string;
  claimantName: string;
  infringingUrl: string;
}): { subject: string; html: string; text: string } {
  const subject = `DMCA notice received — reference ${params.requestId}`;
  const text = [
    `Hello ${params.claimantName},`,
    '',
    'We received your DMCA takedown notice and assigned the following reference ID:',
    params.requestId,
    '',
    `Reported URL: ${params.infringingUrl}`,
    '',
    'Our team will review the notice and follow up if additional information is required.',
    '',
    '— LOAMIFY Legal',
  ].join('\n');

  const html = [
    `<p>Hello ${escapeHtml(params.claimantName)},</p>`,
    '<p>We received your DMCA takedown notice. Your reference ID is:</p>',
    `<p><strong>${escapeHtml(params.requestId)}</strong></p>`,
    `<p>Reported URL: ${escapeHtml(params.infringingUrl)}</p>`,
    '<p>Our team will review the notice and follow up if additional information is required.</p>',
    '<p>— LOAMIFY Legal</p>',
  ].join('');

  return { subject, html, text };
}

export function buildDmcaResolutionMail(params: DmcaMailTemplateParams): {
  subject: string;
  html: string;
  text: string;
} {
  const subject = `DMCA notice ${params.statusLabel} — reference ${params.requestId}`;
  const notes = params.adminNotes?.trim()
    ? `\n\nNotes from our team:\n${params.adminNotes.trim()}`
    : '';

  const text = [
    `Hello ${params.claimantName},`,
    '',
    `Your DMCA request (${params.requestId}) was updated to: ${params.statusLabel}.`,
    `Reported URL: ${params.infringingUrl}`,
    notes,
    '',
    '— LOAMIFY Legal',
  ]
    .filter(Boolean)
    .join('\n');

  const html = [
    `<p>Hello ${escapeHtml(params.claimantName)},</p>`,
    `<p>Your DMCA request (<strong>${escapeHtml(params.requestId)}</strong>) was updated to: <strong>${escapeHtml(params.statusLabel)}</strong>.</p>`,
    `<p>Reported URL: ${escapeHtml(params.infringingUrl)}</p>`,
    params.adminNotes?.trim()
      ? `<p>Notes from our team:<br>${escapeHtml(params.adminNotes.trim())}</p>`
      : '',
    '<p>— LOAMIFY Legal</p>',
  ]
    .filter(Boolean)
    .join('');

  return { subject, html, text };
}

export function buildDmcaCounterNoticeMail(params: {
  requestId: string;
  claimantName: string;
  parentRequestId: string;
  infringingUrl: string;
}): { subject: string; html: string; text: string } {
  const subject = `DMCA counter-notice received — reference ${params.requestId}`;
  const text = [
    `Hello ${params.claimantName},`,
    '',
    'We received your DMCA counter-notice for the following reference:',
    params.parentRequestId,
    '',
    `Counter-notice ID: ${params.requestId}`,
    `Reported URL: ${params.infringingUrl}`,
    '',
    'We will notify the original claimant and follow statutory timelines.',
    '',
    '— LOAMIFY Legal',
  ].join('\n');

  const html = [
    `<p>Hello ${escapeHtml(params.claimantName)},</p>`,
    `<p>We received your DMCA counter-notice related to request <strong>${escapeHtml(params.parentRequestId)}</strong>.</p>`,
    `<p>Counter-notice ID: <strong>${escapeHtml(params.requestId)}</strong></p>`,
    `<p>Reported URL: ${escapeHtml(params.infringingUrl)}</p>`,
    '<p>We will notify the original claimant and follow statutory timelines.</p>',
    '<p>— LOAMIFY Legal</p>',
  ].join('');

  return { subject, html, text };
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
